/**
 * O gráfico de uma resposta do agente (`W-05`, `W-11`; ADR-046), desenhado aqui, do dado.
 *
 * **O dado vem do servidor, o desenho é do navegador.** A ficha traz o gráfico como números —
 * calculados no código, os mesmos das linhas da ficha — e esta tela o desenha: nítido em
 * qualquer largura (a imagem de 1200 px ficava com letra de 5 px no celular), com o valor ao
 * toque e a barra que abre a fila que ela conta (§4.1: todo número abre o que o produziu). O
 * WhatsApp, que não desenha nada, recebe a imagem que o servidor faz do mesmo dado.
 *
 * As formas são as mesmas da imagem: o **acumulado com a previsão** (a linha cheia é o que já
 * passou, a tracejada é a previsão, a lavagem é a faixa), o **ranking** (barras relativas ao
 * maior, nunca ao total) e a **série por dia** (barras) — e, da exploração (`W-14`), as
 * **colunas** na ordem natural (hora, dia da semana, mês), as **linhas** (até três séries e
 * "Outras", com legenda e a régua que mostra todas no mesmo dia) e o **mapa de calor** de
 * duas quebras. Os números ficam na cor do texto; a cor é da série, nunca do valor.
 *
 * `altura` é a da tela cheia (a célula ampliada): o mesmo desenho, maior.
 */
import { useEffect, useState } from "react";
import type { PointerEvent as EventoDePonteiro, ReactNode } from "react";
import { Link } from "react-router";

import type {
  GraficoDaFicha,
  GraficoDeColunasDaFicha,
  GraficoDeLinhasDaFicha,
  GraficoDeMatrizDaFicha,
  GraficoDeProjecaoDaFicha,
  GraficoDeRankingDaFicha,
  GraficoDeSerieDaFicha,
} from "../api/tipos";
import { numeroLongo, percentual, reais, reaisCompacto } from "../formato";

type Unidade = GraficoDaFicha["unidade"];

export function GraficoDoAgente({
  grafico,
  aoAbrir,
  tituloRepetido = false,
  altura = ALTURA,
}: {
  grafico: GraficoDaFicha;
  aoAbrir?: (() => void) | undefined;
  /** O título do gráfico é o da ficha logo acima: não se escreve duas vezes. */
  tituloRepetido?: boolean;
  /** A altura do desenho: maior na tela cheia. */
  altura?: number;
}) {
  return (
    <figure
      aria-label={grafico.descricao}
      className="grid content-start gap-2 self-start rounded-botao border border-linha-suave bg-painel px-2.5 py-2"
    >
      <figcaption className="grid gap-0.5">
        {!tituloRepetido && (
          <span className="text-[11.5px] leading-tight font-semibold text-tinta">
            {grafico.titulo}
          </span>
        )}
        {grafico.subtitulo && (
          <span className="text-[10.5px] leading-tight text-tinta-3">
            {grafico.subtitulo}
          </span>
        )}
      </figcaption>
      {grafico.tipo === "ranking" ? (
        <Ranking grafico={grafico} aoAbrir={aoAbrir} />
      ) : grafico.tipo === "serie" ? (
        <Serie grafico={grafico} altura={altura} />
      ) : grafico.tipo === "colunas" ? (
        <Colunas grafico={grafico} altura={altura} />
      ) : grafico.tipo === "linhas" ? (
        <Linhas grafico={grafico} altura={altura} />
      ) : grafico.tipo === "matriz" ? (
        <Matriz grafico={grafico} amplo={altura > ALTURA} />
      ) : (
        <Projecao grafico={grafico} altura={altura} />
      )}
    </figure>
  );
}

// ------------------------------------------------------------------------- números e datas

/** O percentual chega em centésimos de ponto (9870 é 98,7%): inteiro, como todo valor de
 *  gráfico — a conta é do servidor. */
function curto(valor: number, unidade: Unidade): string {
  if (unidade === "percentual") return percentual(valor / 100);
  return unidade === "reais"
    ? reaisCompacto(valor)
    : Math.round(valor).toLocaleString("pt-BR", {
        notation: valor >= 10_000 ? "compact" : "standard",
        maximumFractionDigits: 1,
      });
}

function longo(valor: number, unidade: Unidade): string {
  if (unidade === "percentual") return percentual(valor / 100);
  return unidade === "reais" ? reais(valor) : numeroLongo(valor);
}

/** As datas vêm como `AAAA-MM-DD`, sem hora: contadas em UTC, nenhum fuso as desloca. */
function emDias(iso: string): number {
  const [a, m, d] = iso.split("-").map(Number);
  return Date.UTC(a!, m! - 1, d!) / 86_400_000;
}

function ddmm(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

const SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function diaDaSemana(iso: string): string {
  return SEMANA[new Date(emDias(iso) * 86_400_000).getUTCDay()]!;
}

/** O rótulo do eixo: o curto, e o zero sem centavos ("R$ 0", não "R$ 0,00"). */
function rotuloDoEixo(valor: number, unidade: Unidade): string {
  if (valor === 0) return unidade === "reais" ? "R$ 0" : "0";
  return curto(valor, unidade);
}

const LINHAS_DA_GRADE = [0, 1 / 3, 2 / 3, 1];

/** A margem esquerda que cabe o maior rótulo do eixo. Fixa, ela cortava o "R$ 1" de
 *  "R$ 10,0 mil" na série em reais (a perda por mau tempo, `W-06`) — e sobrava "0,0 mil",
 *  um número errado no topo do gráfico. A largura por caractere é a da fonte de 9,5 px. */
function margemDoEixo(maximo: number, unidade: Unidade): number {
  const maior = Math.max(
    ...LINHAS_DA_GRADE.map((f) => rotuloDoEixo(maximo * f, unidade).length),
  );
  return Math.max(30, Math.ceil(maior * 5.6) + 8);
}

/** O passo da grade em número redondo (1, 2, 2,5 ou 5 vezes uma potência de dez). */
function teto(maximo: number): number {
  if (maximo <= 0) return 1;
  const bruto = (maximo * 1.05) / 3;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const passo =
    [1, 2, 2.5, 5, 10].map((f) => f * potencia).find((p) => p >= bruto) ??
    10 * potencia;
  return passo * Math.ceil((maximo * 1.05) / passo);
}

// ------------------------------------------------------------------------- o quadro

/** A largura do contêiner, medida — o SVG desenha em pixels de verdade, e o texto dele
 *  fica do tamanho que tem, em vez de encolher junto com um `viewBox`. */
function useLargura(): [(no: HTMLDivElement | null) => void, number] {
  const [caixa, definirCaixa] = useState<HTMLDivElement | null>(null);
  const [largura, definirLargura] = useState(360);
  useEffect(() => {
    if (!caixa) return;
    const medir = () => definirLargura(Math.max(240, caixa.clientWidth));
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [caixa]);
  return [definirCaixa, largura];
}

const ALTURA = 150;
const TOPO = 10;
const BASE = 20;

function Grade({
  largura,
  esquerda,
  maximo,
  unidade,
  altura = ALTURA,
  minimo = 0,
}: {
  largura: number;
  esquerda: number;
  maximo: number;
  unidade: Unidade;
  altura?: number;
  /** O chão do eixo: zero nas barras, sempre; a linha da taxa pode começar acima (§3). */
  minimo?: number;
}) {
  const y = (v: number) =>
    TOPO + (altura - TOPO - BASE) * (1 - (v - minimo) / (maximo - minimo));
  return (
    <g aria-hidden="true">
      {LINHAS_DA_GRADE.map((f) => {
        const v = minimo + (maximo - minimo) * f;
        return (
          <g key={f}>
            <line
              x1={esquerda}
              x2={largura}
              y1={y(v)}
              y2={y(v)}
              className="stroke-linha-suave"
              strokeWidth={1}
            />
            <text
              x={esquerda - 6}
              y={y(v) + 3}
              textAnchor="end"
              className="fill-tinta-3 text-[9.5px]"
            >
              {rotuloDoEixo(v, unidade)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function Dica({ x, children }: { x: number; children: ReactNode }) {
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-0 z-10 max-w-[70%] -translate-x-1/2 rounded-botao border border-linha bg-painel-2 px-2 py-1 text-[11px] leading-snug whitespace-nowrap text-tinta shadow-lg"
      style={{ left: x }}
    >
      {children}
    </div>
  );
}

/** Onde a dica fica: centrada no ponto, mas sem passar da borda. */
function dentro(x: number, largura: number): number {
  return Math.min(Math.max(x, 70), largura - 70);
}

// ------------------------------------------------------------------------- ranking

function Ranking({
  grafico,
  aoAbrir,
}: {
  grafico: GraficoDeRankingDaFicha;
  aoAbrir?: (() => void) | undefined;
}) {
  const maior = Math.max(1, ...grafico.itens.map((i) => i.valor));
  const cor = grafico.cor === "vermelho" ? "bg-vermelho" : "bg-azul";
  return (
    <ol className="grid gap-1">
      {grafico.itens.map((item) => {
        const corpo = (
          <>
            <span className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate text-[11.5px] text-tinta-2">
                {item.rotulo}
              </span>
              <span
                className="mono text-[12px] font-bold text-tinta"
                title={longo(item.valor, grafico.unidade)}
              >
                {curto(item.valor, grafico.unidade)}
              </span>
            </span>
            <span className="block h-2 rounded-full bg-painel-2">
              <span
                className={`block h-2 rounded-full ${cor}`}
                style={{
                  width: `${Math.max(2, (item.valor / maior) * 100)}%`,
                }}
              />
            </span>
            {item.detalhe && (
              <small className="block truncate text-[10px] text-tinta-3">
                {item.detalhe}
              </small>
            )}
          </>
        );
        return (
          <li key={item.rotulo}>
            {/* Só caminho do cockpit vira link (a mesma regra da célula). */}
            {item.link?.startsWith("/") && !item.link.startsWith("//") ? (
              <Link
                to={item.link}
                onClick={aoAbrir}
                title={`Abrir “${item.rotulo}”`}
                className="grid min-h-10 gap-1 rounded-botao px-1.5 py-1 hover:bg-realce"
              >
                {corpo}
              </Link>
            ) : (
              <div className="grid gap-1 px-1.5 py-1">{corpo}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------------------------- série por dia

function Serie({
  grafico,
  altura: ALTURA,
}: {
  grafico: GraficoDeSerieDaFicha;
  altura: number;
}) {
  const [medir, largura] = useLargura();
  const [ativo, definirAtivo] = useState<number | null>(null);
  const pontos = grafico.pontos;
  if (pontos.length === 0) return null;

  const maximo = teto(Math.max(...pontos.map((p) => p.valor)));
  const esquerda = margemDoEixo(maximo, grafico.unidade);
  const primeiro = emDias(pontos[0]!.dia);
  const dias = emDias(pontos[pontos.length - 1]!.dia) - primeiro + 1;
  const faixa = (largura - esquerda) / dias;
  const barra = Math.max(1.5, Math.min(faixa - 2, 22));
  const util = ALTURA - TOPO - BASE;
  const x = (iso: string) => esquerda + faixa * (emDias(iso) - primeiro + 0.5);
  const y = (v: number) => TOPO + util * (1 - v / maximo);
  const marcas = [0, Math.floor((pontos.length - 1) / 2), pontos.length - 1];
  const total = pontos.reduce((s, p) => s + p.valor, 0);
  // Vermelho é perda (a perda por mau tempo, `W-06`), como no ranking de perdas.
  const [cheia, esmaecida] =
    grafico.cor === "vermelho"
      ? ["fill-vermelho", "fill-vermelho/45"]
      : ["fill-azul", "fill-azul/45"];

  function apontar(e: EventoDePonteiro<SVGSVGElement>) {
    const caixa = e.currentTarget.getBoundingClientRect();
    const dia = Math.floor((e.clientX - caixa.left - esquerda) / faixa);
    const i = pontos.findIndex((p) => emDias(p.dia) - primeiro === dia);
    definirAtivo(i >= 0 ? i : null);
  }

  const atual = ativo === null ? null : pontos[ativo]!;
  return (
    <div className="grid gap-1">
      <p className="flex items-baseline gap-1.5">
        <span className="mono text-[17px] font-bold text-tinta">
          {curto(total, grafico.unidade)}
        </span>
        <span className="text-[10.5px] text-tinta-3">
          {grafico.medida && `${grafico.medida} · `}
          {pontos.length} dias
        </span>
      </p>
      <div ref={medir} className="relative min-w-0">
        {atual && (
          <Dica x={dentro(x(atual.dia), largura)}>
            {diaDaSemana(atual.dia)}, {ddmm(atual.dia)}:{" "}
            <b className="mono">{longo(atual.valor, grafico.unidade)}</b>
            {grafico.medida && ` ${grafico.medida}`}
          </Dica>
        )}
        <svg
          width={largura}
          height={ALTURA}
          className="block touch-pan-y"
          aria-hidden="true"
          onPointerMove={apontar}
          onPointerDown={apontar}
          onPointerLeave={() => definirAtivo(null)}
        >
          <Grade
            largura={largura}
            esquerda={esquerda}
            maximo={maximo}
            unidade={grafico.unidade}
            altura={ALTURA}
          />
          {pontos.map((p, i) => {
            if (p.valor <= 0) return null;
            const topo = y(p.valor);
            const raio = Math.min(3, barra / 2);
            const esq = x(p.dia) - barra / 2;
            const dir = esq + barra;
            const chao = TOPO + util;
            // A ponta arredondada, a base reta — presa à linha do zero (§3).
            const d = `M ${esq} ${chao} V ${topo + raio} Q ${esq} ${topo} ${esq + raio} ${topo} H ${dir - raio} Q ${dir} ${topo} ${dir} ${topo + raio} V ${chao} Z`;
            return (
              <path
                key={p.dia}
                d={d}
                className={ativo === null || ativo === i ? cheia : esmaecida}
              />
            );
          })}
          {marcas
            .filter((m, i, todas) => todas.indexOf(m) === i)
            .map((i) => (
              <text
                key={i}
                x={x(pontos[i]!.dia)}
                y={ALTURA - 5}
                textAnchor={
                  i === 0 ? "start" : i === pontos.length - 1 ? "end" : "middle"
                }
                className="fill-tinta-3 text-[9.5px]"
              >
                {ddmm(pontos[i]!.dia)}
              </text>
            ))}
        </svg>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- projeção

interface PontoDaDica {
  dia: string;
  texto: string;
  valor: number;
  faixa?: readonly [number, number];
}

function Projecao({
  grafico,
  altura: ALTURA,
}: {
  grafico: GraficoDeProjecaoDaFicha;
  altura: number;
}) {
  const [medir, largura] = useLargura();
  const [ativo, definirAtivo] = useState<number | null>(null);
  const { realizado, previsto } = grafico;
  const fim = previsto[previsto.length - 1];

  const maximo = teto(
    Math.max(
      1,
      ...realizado.map((p) => p.valor),
      ...previsto.map((p) => p.maximo),
    ),
  );
  const esquerda = margemDoEixo(maximo, grafico.unidade);
  const inicio = emDias(grafico.inicio);
  const dias = Math.max(1, emDias(grafico.fim) - inicio);
  const util = ALTURA - TOPO - BASE;
  const x = (iso: string) =>
    esquerda + (largura - esquerda - 6) * ((emDias(iso) - inicio) / dias);
  const y = (v: number) => TOPO + util * (1 - v / maximo);
  const linha = (pts: { dia: string; v: number }[]) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.dia)} ${y(p.v)}`).join(" ");
  const faixa =
    previsto.length > 1
      ? `${previsto.map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.dia)} ${y(p.maximo)}`).join(" ")} ${[
          ...previsto,
        ]
          .reverse()
          .map((p) => `L ${x(p.dia)} ${y(p.minimo)}`)
          .join(" ")} Z`
      : null;
  const hoje = emDias(grafico.hoje);
  const hojeVisivel = hoje >= inicio && hoje <= inicio + dias;

  // A dica segue o dia sob o dedo: realizado até ontem, previsão daí em diante.
  const todos: PontoDaDica[] = [
    ...realizado.map((p) => ({
      dia: p.dia,
      texto: "realizado",
      valor: p.valor,
    })),
    ...previsto.slice(realizado.length ? 1 : 0).map((p) => ({
      dia: p.dia,
      texto: "previsão",
      valor: p.central,
      faixa: [p.minimo, p.maximo] as const,
    })),
  ];
  function apontar(e: EventoDePonteiro<SVGSVGElement>) {
    if (todos.length === 0) return;
    const caixa = e.currentTarget.getBoundingClientRect();
    const alvo =
      inicio +
      ((e.clientX - caixa.left - esquerda) / (largura - esquerda - 6)) * dias;
    let melhor = 0;
    todos.forEach((p, i) => {
      if (
        Math.abs(emDias(p.dia) - alvo) <
        Math.abs(emDias(todos[melhor]!.dia) - alvo)
      )
        melhor = i;
    });
    definirAtivo(melhor);
  }
  const atual = ativo === null ? null : todos[ativo]!;

  return (
    <div className="grid gap-1">
      {fim && (
        <p className="grid gap-0.5">
          <span className="mono text-[17px] font-bold text-tinta">
            Previsão: {curto(fim.central, grafico.unidade)}
          </span>
          <span className="text-[10.5px] text-tinta-3">
            entre {curto(fim.minimo, grafico.unidade)} e{" "}
            {curto(fim.maximo, grafico.unidade)} em {ddmm(fim.dia)}
            {realizado.length > 0 &&
              ` · realizado até ${ddmm(realizado[realizado.length - 1]!.dia)}: ${curto(
                realizado[realizado.length - 1]!.valor,
                grafico.unidade,
              )}`}
          </span>
        </p>
      )}
      <div ref={medir} className="relative min-w-0">
        {atual && (
          <Dica x={dentro(x(atual.dia), largura)}>
            {ddmm(atual.dia)} · {atual.texto}:{" "}
            <b className="mono">{curto(atual.valor, grafico.unidade)}</b>
            {atual.faixa &&
              ` (entre ${curto(atual.faixa[0], grafico.unidade)} e ${curto(atual.faixa[1], grafico.unidade)})`}
          </Dica>
        )}
        <svg
          width={largura}
          height={ALTURA}
          className="block touch-pan-y"
          aria-hidden="true"
          onPointerMove={apontar}
          onPointerDown={apontar}
          onPointerLeave={() => definirAtivo(null)}
        >
          <Grade
            largura={largura}
            esquerda={esquerda}
            maximo={maximo}
            unidade={grafico.unidade}
            altura={ALTURA}
          />
          {hojeVisivel && (
            <g>
              <line
                x1={x(grafico.hoje)}
                x2={x(grafico.hoje)}
                y1={TOPO}
                y2={TOPO + util}
                className="stroke-tinta-3"
                strokeWidth={1}
                strokeDasharray="2 3"
              />
              <text
                x={x(grafico.hoje) + 4}
                y={TOPO + 8}
                className="fill-tinta-3 text-[9.5px]"
              >
                hoje
              </text>
            </g>
          )}
          {faixa && <path d={faixa} className="fill-azul/20" />}
          {realizado.length > 1 && (
            <path
              d={linha(realizado.map((p) => ({ dia: p.dia, v: p.valor })))}
              fill="none"
              className="stroke-azul"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          )}
          {previsto.length > 1 && (
            <path
              d={linha(previsto.map((p) => ({ dia: p.dia, v: p.central })))}
              fill="none"
              className="stroke-azul"
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          )}
          {[realizado[realizado.length - 1], fim]
            .filter((p) => p !== undefined)
            .map((p, i) => (
              <circle
                key={i}
                cx={x(p.dia)}
                cy={y("valor" in p ? p.valor : p.central)}
                r={4}
                strokeWidth={2}
                className="fill-azul stroke-painel"
              />
            ))}
          {atual && (
            <line
              x1={x(atual.dia)}
              x2={x(atual.dia)}
              y1={TOPO}
              y2={TOPO + util}
              className="stroke-tinta-2"
              strokeWidth={1}
            />
          )}
          <text
            x={esquerda}
            y={ALTURA - 5}
            className="fill-tinta-3 text-[9.5px]"
          >
            {ddmm(grafico.inicio)}
          </text>
          <text
            x={largura - 6}
            y={ALTURA - 5}
            textAnchor="end"
            className="fill-tinta-3 text-[9.5px]"
          >
            {ddmm(grafico.fim)}
          </text>
        </svg>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- colunas (`W-14`)

/** A cor da barra: azul, ou vermelho quando a grandeza é perda. */
function corDaBarra(cor: "azul" | "vermelho"): [string, string] {
  return cor === "vermelho"
    ? ["fill-vermelho", "fill-vermelho/45"]
    : ["fill-azul", "fill-azul/45"];
}

/** Quantos rótulos de eixo cabem: um a cada `passo`, sem um encostar no outro. */
function passoDosRotulos(rotulos: string[], faixa: number): number {
  const maior = Math.max(1, ...rotulos.map((r) => r.length));
  return Math.max(1, Math.ceil((maior * 5.8 + 6) / Math.max(faixa, 1)));
}

/** Uma grandeza por categoria na ordem dela — hora, dia da semana, mês, categoria. */
function Colunas({
  grafico,
  altura,
}: {
  grafico: GraficoDeColunasDaFicha;
  altura: number;
}) {
  const [medir, largura] = useLargura();
  const [ativo, definirAtivo] = useState<number | null>(null);
  const colunas = grafico.colunas;
  if (colunas.length === 0) return null;

  const maior = Math.max(...colunas.map((c) => c.valor));
  // A taxa acima da metade vai até 100%, e não a "150%" que o passo redondo daria.
  const maximo =
    grafico.unidade === "percentual" && maior > 5_000 && maior <= 10_000
      ? 10_000
      : teto(maior);
  const esquerda = margemDoEixo(maximo, grafico.unidade);
  const faixa = (largura - esquerda) / colunas.length;
  // A barra fina e o vão de 2 px entre elas (§3): a cor é da grandeza, o vão é da superfície.
  const barra = Math.max(2, Math.min(faixa - 2, 28));
  const util = altura - TOPO - BASE;
  const x = (i: number) => esquerda + faixa * (i + 0.5);
  const y = (v: number) => TOPO + util * (1 - v / maximo);
  const passo = passoDosRotulos(
    colunas.map((c) => c.rotulo),
    faixa,
  );
  const [cheia, esmaecida] = corDaBarra(grafico.cor);

  function apontar(e: EventoDePonteiro<SVGSVGElement>) {
    const caixa = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - caixa.left - esquerda) / faixa);
    definirAtivo(i >= 0 && i < colunas.length ? i : null);
  }
  const atual = ativo === null ? null : colunas[ativo]!;

  return (
    <div ref={medir} className="relative min-w-0">
      {atual && ativo !== null && (
        <Dica x={dentro(x(ativo), largura)}>
          {atual.rotulo}:{" "}
          <b className="mono">{longo(atual.valor, grafico.unidade)}</b>
          {grafico.medida && ` ${grafico.medida}`}
        </Dica>
      )}
      <svg
        width={largura}
        height={altura}
        className="block touch-pan-y"
        aria-hidden="true"
        onPointerMove={apontar}
        onPointerDown={apontar}
        onPointerLeave={() => definirAtivo(null)}
      >
        <Grade
          largura={largura}
          esquerda={esquerda}
          maximo={maximo}
          unidade={grafico.unidade}
          altura={altura}
        />
        {colunas.map((c, i) => {
          if (c.valor <= 0) return null;
          const topo = y(c.valor);
          const raio = Math.min(3, barra / 2);
          const esq = x(i) - barra / 2;
          const dir = esq + barra;
          const chao = TOPO + util;
          const d = `M ${esq} ${chao} V ${topo + raio} Q ${esq} ${topo} ${esq + raio} ${topo} H ${dir - raio} Q ${dir} ${topo} ${dir} ${topo + raio} V ${chao} Z`;
          return (
            <path
              key={c.rotulo}
              d={d}
              className={ativo === null || ativo === i ? cheia : esmaecida}
            />
          );
        })}
        {colunas.map((c, i) =>
          i % passo === 0 ? (
            <text
              key={c.rotulo}
              x={x(i)}
              y={altura - 5}
              textAnchor="middle"
              className="fill-tinta-3 text-[9.5px]"
            >
              {c.rotulo}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}

// ------------------------------------------------------------------------- linhas (`W-14`)

/** As cores das séries, em ordem fixa — a cor segue a série, nunca a posição no ranking; a
 *  quarta é sempre "Outras", em cinza (tokens `serie-*` e `outras`, design system §3). */
// Os nomes inteiros, e não montados: o Tailwind só gera a classe que lê escrita no código.
const CORES_DAS_SERIES: [string, string, string][] = [
  ["stroke-serie-1", "bg-serie-1", "fill-serie-1"],
  ["stroke-serie-2", "bg-serie-2", "fill-serie-2"],
  ["stroke-serie-3", "bg-serie-3", "fill-serie-3"],
];
const COR_DE_OUTRAS: [string, string, string] = [
  "stroke-outras",
  "bg-outras",
  "fill-outras",
];

/** `[traço, fundo, preenchimento]` da série. */
function corDaSerie(nome: string, i: number): [string, string, string] {
  if (nome === "Outras" || i >= CORES_DAS_SERIES.length) return COR_DE_OUTRAS;
  return CORES_DAS_SERIES[i]!;
}

/** O eixo de uma linha: do zero, em contagem e reais; na taxa, colado nos dados — uma taxa
 *  entre 98,4% e 98,7% vista de 0 a 100 é uma reta, e a pergunta é justamente a variação. */
function eixoDaLinha(
  valores: number[],
  unidade: Unidade,
): { minimo: number; maximo: number } {
  if (valores.length === 0) return { minimo: 0, maximo: 1 };
  const maior = Math.max(...valores);
  const menor = Math.min(...valores);
  if (unidade !== "percentual" || menor <= 0) {
    return { minimo: 0, maximo: teto(maior) };
  }
  // Três passos redondos que cobrem os dados — a grade cai em 97,0%, 98,0%, 99,0% e não em
  // 97,4%. O vão mínimo é de 0,3 ponto: abaixo disso, a variação é ruído desenhado grande.
  const bruto = Math.max((maior - menor) * 1.2, 30) / 3;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  for (const fator of [1, 2, 2.5, 5, 10, 20, 25, 50]) {
    const passo = fator * potencia;
    if (passo < bruto) continue;
    let minimo = Math.floor(menor / passo) * passo;
    if (minimo + 3 * passo < maior) continue;
    // O teto é 100%: a grade encosta nele em vez de passar.
    if (minimo + 3 * passo > 10_000) minimo = Math.max(0, 10_000 - 3 * passo);
    return { minimo, maximo: minimo + 3 * passo };
  }
  return { minimo: 0, maximo: teto(maior) };
}

function Linhas({
  grafico,
  altura,
}: {
  grafico: GraficoDeLinhasDaFicha;
  altura: number;
}) {
  const [medir, largura] = useLargura();
  const [ativo, definirAtivo] = useState<number | null>(null);
  const { eixo, series } = grafico;
  if (eixo.length === 0 || series.length === 0) return null;

  const valores = series.flatMap((s) =>
    s.valores.filter((v): v is number => v !== null),
  );
  const { minimo, maximo } = eixoDaLinha(valores, grafico.unidade);
  const esquerda = margemDoEixo(maximo, grafico.unidade);
  const util = altura - TOPO - BASE;
  const passoX = (largura - esquerda - 8) / Math.max(1, eixo.length - 1);
  const x = (i: number) =>
    eixo.length === 1 ? (largura + esquerda) / 2 : esquerda + 4 + passoX * i;
  const y = (v: number) =>
    TOPO + util * (1 - (v - minimo) / Math.max(1, maximo - minimo));
  const rotulo = (i: number) =>
    grafico.eixo_de_datas ? ddmm(eixo[i]!) : eixo[i]!;
  const marcas =
    eixo.length <= 7
      ? eixo.map((_, i) => i)
      : [0, Math.floor((eixo.length - 1) / 2), eixo.length - 1];

  /** A linha se interrompe onde a série não tem dado: o buraco não vira uma reta. */
  function trecho(valoresDaSerie: (number | null)[]): string {
    let d = "";
    let caneta = false;
    valoresDaSerie.forEach((v, i) => {
      if (v === null) {
        caneta = false;
        return;
      }
      d += `${caneta ? "L" : "M"} ${x(i)} ${y(v)} `;
      caneta = true;
    });
    return d.trim();
  }

  function apontar(e: EventoDePonteiro<SVGSVGElement>) {
    const caixa = e.currentTarget.getBoundingClientRect();
    const i = Math.round((e.clientX - caixa.left - esquerda - 4) / passoX);
    definirAtivo(Math.min(eixo.length - 1, Math.max(0, i)));
  }

  return (
    <div className="grid gap-1.5">
      {series.length > 1 && (
        <ul
          className="flex flex-wrap gap-x-3 gap-y-1"
          aria-label="Legenda das séries"
        >
          {series.map((s, i) => (
            <li
              key={s.nome}
              className="flex items-center gap-1.5 text-[10.5px] text-tinta-2"
            >
              <span
                className={`h-[3px] w-3.5 rounded-full ${corDaSerie(s.nome, i)[1]}`}
                aria-hidden="true"
              />
              <span className="max-w-[16ch] truncate" title={s.nome}>
                {s.nome}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div ref={medir} className="relative min-w-0">
        {ativo !== null && (
          <Dica x={dentro(x(ativo), largura)}>
            <span className="block text-tinta-3">
              {grafico.eixo_de_datas
                ? `${diaDaSemana(eixo[ativo]!)}, ${ddmm(eixo[ativo]!)}`
                : eixo[ativo]}
            </span>
            {series.map((s, i) => (
              <span key={s.nome} className="flex items-center gap-1.5">
                <span
                  className={`h-[3px] w-2.5 rounded-full ${corDaSerie(s.nome, i)[1]}`}
                  aria-hidden="true"
                />
                {series.length > 1 && (
                  <span className="max-w-[14ch] truncate">{s.nome}:</span>
                )}
                <b className="mono">
                  {s.valores[ativo] === null
                    ? "—"
                    : longo(s.valores[ativo]!, grafico.unidade)}
                </b>
              </span>
            ))}
          </Dica>
        )}
        <svg
          width={largura}
          height={altura}
          className="block touch-pan-y"
          aria-hidden="true"
          onPointerMove={apontar}
          onPointerDown={apontar}
          onPointerLeave={() => definirAtivo(null)}
        >
          <Grade
            largura={largura}
            esquerda={esquerda}
            maximo={maximo}
            minimo={minimo}
            unidade={grafico.unidade}
            altura={altura}
          />
          {ativo !== null && (
            <line
              x1={x(ativo)}
              x2={x(ativo)}
              y1={TOPO}
              y2={TOPO + util}
              className="stroke-tinta-3"
              strokeWidth={1}
            />
          )}
          {series.map((s, i) => (
            <path
              key={s.nome}
              d={trecho(s.valores)}
              fill="none"
              className={corDaSerie(s.nome, i)[0]}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          {/* O ponto solto (vizinhos sem dado) não tem linha: vira um ponto. E o ponto sob o
              dedo, com o anel da superfície em volta (§3). */}
          {series.map((s, i) =>
            s.valores.map((v, j) => {
              const solto =
                v !== null &&
                (s.valores[j - 1] ?? null) === null &&
                (s.valores[j + 1] ?? null) === null;
              if (v === null || (!solto && ativo !== j)) return null;
              return (
                <circle
                  key={`${s.nome}-${j}`}
                  cx={x(j)}
                  cy={y(v)}
                  r={4}
                  strokeWidth={2}
                  className={`${corDaSerie(s.nome, i)[2]} stroke-painel`}
                />
              );
            }),
          )}
          {marcas.map((i) => (
            <text
              key={i}
              x={x(i)}
              y={altura - 5}
              textAnchor={
                eixo.length <= 7
                  ? "middle"
                  : i === 0
                    ? "start"
                    : i === eixo.length - 1
                      ? "end"
                      : "middle"
              }
              className="fill-tinta-3 text-[9.5px]"
            >
              {rotulo(i)}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- mapa de calor (`W-14`)

const CALOR = [
  "bg-calor-1",
  "bg-calor-2",
  "bg-calor-3",
  "bg-calor-4",
  "bg-calor-5",
  "bg-calor-6",
  "bg-calor-7",
];

/** Duas quebras ao mesmo tempo: um tom só, do escuro (pouco) ao claro (muito). Em HTML, e
 *  não em SVG: o rótulo da linha e o da coluna ficam texto de verdade, e a grade se ajusta à
 *  largura sem escala. */
function Matriz({
  grafico,
  amplo,
}: {
  grafico: GraficoDeMatrizDaFicha;
  amplo: boolean;
}) {
  const [ativo, definirAtivo] = useState<[number, number] | null>(null);
  const numeros = grafico.valores.flat().filter((v): v is number => v !== null);
  if (numeros.length === 0) return null;
  const menor = Math.min(...numeros);
  const maior = Math.max(...numeros);
  const tom = (v: number) =>
    CALOR[
      maior > menor
        ? Math.min(6, Math.floor(((v - menor) / (maior - menor)) * 7))
        : 6
    ]!;
  const colunas = grafico.colunas.length;
  // Rótulo de coluna a cada `passo`: 24 horas cabem uma sim, uma não, no celular.
  const passo = colunas > 12 && !amplo ? 3 : colunas > 12 ? 2 : 1;
  const celula = amplo ? "h-6" : "h-[18px]";
  const atual =
    ativo === null
      ? null
      : {
          linha: grafico.linhas[ativo[0]]!,
          coluna: grafico.colunas[ativo[1]]!,
          valor: grafico.valores[ativo[0]]![ativo[1]] ?? null,
        };

  return (
    <div className="grid gap-1.5">
      <p className="min-h-4 text-[11px] text-tinta-2" role="status">
        {atual ? (
          <>
            {atual.linha} · {atual.coluna}:{" "}
            <b className="mono text-tinta">
              {atual.valor === null ? "—" : longo(atual.valor, grafico.unidade)}
            </b>
          </>
        ) : (
          <span className="text-tinta-3">
            Toque numa célula para ver o número.
          </span>
        )}
      </p>
      <div
        className="grid min-w-0 items-center gap-x-1.5 gap-y-[2px]"
        style={{ gridTemplateColumns: "minmax(0, 30%) 1fr" }}
        onPointerLeave={() => definirAtivo(null)}
      >
        <span />
        <div
          className="grid gap-[2px]"
          style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}
          aria-hidden="true"
        >
          {/* O rótulo ocupa as `passo` colunas que ele representa: "0h" sobre 0h–2h, sem
              cortar em "0." numa coluna de oito pixels. */}
          {grafico.colunas.map((c, j) =>
            j % passo === 0 ? (
              <span
                key={c}
                className="truncate text-left text-[9px] text-tinta-3"
                style={{
                  gridColumn: `${j + 1} / span ${Math.min(passo, colunas - j)}`,
                }}
              >
                {c}
              </span>
            ) : null,
          )}
        </div>
        {grafico.linhas.map((linha, i) => (
          <div key={linha} className="contents">
            <span className="truncate text-[10.5px] text-tinta-2" title={linha}>
              {linha}
            </span>
            <div
              className="grid gap-[2px]"
              style={{
                gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
              }}
            >
              {grafico.valores[i]!.map((v, j) => (
                <span
                  key={j}
                  onPointerEnter={() => definirAtivo([i, j])}
                  onPointerDown={() => definirAtivo([i, j])}
                  className={`${celula} rounded-[2px] ${v === null ? "bg-painel-2" : tom(v)} ${
                    ativo && ativo[0] === i && ativo[1] === j
                      ? "outline-2 outline-tinta"
                      : ""
                  }`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div
        className="flex items-center gap-1.5 text-[9.5px] text-tinta-3"
        aria-hidden="true"
      >
        <span className="mono">{curto(menor, grafico.unidade)}</span>
        <span className="flex h-2 w-24 overflow-hidden rounded-full">
          {CALOR.map((c) => (
            <span key={c} className={`flex-1 ${c}`} />
          ))}
        </span>
        <span className="mono">{curto(maior, grafico.unidade)}</span>
      </div>
    </div>
  );
}
